/**
 * Глобальное хранилище уведомлений портала.
 *
 * Не зависит от React: `notify(...)` можно вызывать из компонентов, хуков, утилит и axios-интерсепторов.
 * Отрисовку делает <NotificationHost /> (монтируется один раз в App).
 */

export type NotifySeverity = 'success' | 'error' | 'warning' | 'info';

export interface NotifyAction {
  label: string;
  onClick: () => void;
}

export interface NotifyOptions {
  message: string;
  title?: string;
  /** По умолчанию info */
  severity?: NotifySeverity;
  /** Время показа, мс. 0 (или Infinity) - не закрывать автоматически. По умолчанию зависит от типа и длины текста */
  duration?: number;
  action?: NotifyAction;
  /** Повторный вызов с тем же id обновляет уведомление (и перезапускает таймер), а не добавляет новое */
  id?: string;
}

export interface NotificationItem {
  /** Уникальный идентификатор экземпляра (ключ React) */
  uid: string;
  /** Ключ дедупликации: явный id или «тип + заголовок + текст» */
  key: string;
  severity: NotifySeverity;
  title?: string;
  message: string;
  /** Итоговое время показа, мс; 0 - без таймера */
  duration: number;
  action?: NotifyAction;
  /** Сколько раз подряд сработало одно и то же уведомление */
  count: number;
  /** Растёт при повторе: перезапускает полосу таймера */
  version: number;
  /** Идёт анимация ухода - после неё уведомление удаляется из списка */
  leaving: boolean;
}

/** Сколько уведомлений видно одновременно; самые старые закрываются, когда набирается больше */
export const MAX_VISIBLE = 5;

const BASE_DURATION: Record<NotifySeverity, number> = {
  success: 4000,
  info: 5000,
  warning: 6500,
  error: 8000
};

/** Длинный текст читается дольше: +40 мс на каждый символ сверх 60, но не больше +6 с */
export function resolveDuration(severity: NotifySeverity, message: string, explicit?: number): number {
  if (explicit !== undefined) {
    return explicit > 0 && Number.isFinite(explicit) ? explicit : 0;
  }
  const extra = Math.min(6000, Math.max(0, message.length - 60) * 40);
  return BASE_DURATION[severity] + extra;
}

let items: NotificationItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach(listener => listener());
const commit = (next: NotificationItem[]) => {
  items = next;
  emit();
};

export const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const getSnapshot = () => items;

function show(options: NotifyOptions): string {
  const message = String(options.message ?? '').trim();
  if (!message) return '';

  const severity: NotifySeverity = options.severity || 'info';
  const title = options.title?.trim() || undefined;
  const explicitId = options.id !== undefined && options.id !== '';
  const key = explicitId ? String(options.id) : `${severity}|${title || ''}|${message}`;
  const duration = resolveDuration(severity, message, options.duration);

  const existing = items.find(item => item.key === key && !item.leaving);
  if (existing) {
    commit(items.map(item => (item.uid === existing.uid
      ? {
        ...item, severity, title, message, duration, action: options.action,
        count: explicitId ? item.count : item.count + 1,
        version: item.version + 1
      }
      : item)));
    return existing.uid;
  }

  const uid = `n${++seq}`;
  let next: NotificationItem[] = [
    ...items,
    { uid, key, severity, title, message, duration, action: options.action, count: 1, version: 0, leaving: false }
  ];

  // Сверх лимита закрываем самые старые из ещё живых
  const overflow = next.filter(item => !item.leaving).length - MAX_VISIBLE;
  if (overflow > 0) {
    let left = overflow;
    next = next.map(item => {
      if (left > 0 && !item.leaving && item.uid !== uid) {
        left--;
        return { ...item, leaving: true };
      }
      return item;
    });
  }

  commit(next);
  return uid;
}

/** Запустить анимацию ухода (по uid или явному id) */
export function dismissNotification(idOrKey: string) {
  if (!items.some(item => (item.uid === idOrKey || item.key === idOrKey) && !item.leaving)) return;
  commit(items.map(item => ((item.uid === idOrKey || item.key === idOrKey) ? { ...item, leaving: true } : item)));
}

export function dismissAllNotifications() {
  if (!items.some(item => !item.leaving)) return;
  commit(items.map(item => (item.leaving ? item : { ...item, leaving: true })));
}

/** Убрать из списка (после завершения анимации ухода) */
export function removeNotification(uid: string) {
  if (!items.some(item => item.uid === uid)) return;
  commit(items.filter(item => item.uid !== uid));
}

type NotifyExtras = Omit<NotifyOptions, 'message' | 'severity'>;

/**
 * Единая точка вызова уведомлений:
 *   notify('Сохранено', 'success')            - как прежние showSnackbar(message, severity)
 *   notify.success('Сохранено')               - короткая форма
 *   notify.error('Не удалось', { title: 'Отчёт', action: { label: 'Повторить', onClick } })
 *   notify({ message, severity, duration: 0 }) - полная форма (duration: 0 - не закрывать само)
 * Возвращает id уведомления - его можно передать в notify.dismiss(id).
 */
export const notify = Object.assign(
  (input: string | NotifyOptions, severity?: NotifySeverity, extras?: NotifyExtras): string => (
    typeof input === 'object' && input !== null
      ? show(input)
      : show({ ...extras, message: input, severity: severity || 'info' })
  ),
  {
    success: (message: string, extras?: NotifyExtras) => show({ ...extras, message, severity: 'success' }),
    error: (message: string, extras?: NotifyExtras) => show({ ...extras, message, severity: 'error' }),
    warning: (message: string, extras?: NotifyExtras) => show({ ...extras, message, severity: 'warning' }),
    info: (message: string, extras?: NotifyExtras) => show({ ...extras, message, severity: 'info' }),
    dismiss: dismissNotification,
    clear: dismissAllNotifications
  }
);

export type Notify = typeof notify;
